import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { memo, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { ACTIVE_FILTERS_BAR_SEGMENT_INNER_PAD_CLASS } from "./activeFiltersBarShared";
import { ActiveFiltersBarSortStatusIcon } from "./ActiveFiltersBarSortStatusIcon";

interface ActiveFiltersBarSortSlotProps {
  segmentClass: string;
  idleLabelClass: string;
  triggerInteractionsClass: string;
  activeGlyphClass: string;
  sortSummaryLine: string | null;
  sortingActiveBriefLabel: string;
  sortDirection: "asc" | "desc" | null;
  noneActiveSortingLabel: string;
  onClearSorting: () => void;
}

const ActiveFiltersBarSortSlotComp = ({
  segmentClass,
  idleLabelClass,
  triggerInteractionsClass,
  activeGlyphClass,
  sortSummaryLine,
  sortingActiveBriefLabel,
  sortDirection,
  noneActiveSortingLabel,
  onClearSorting,
}: ActiveFiltersBarSortSlotProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);

  const handleClearSorting = useCallback(() => {
    onClearSorting();
    setOpen(false);
  }, [onClearSorting]);

  if (sortSummaryLine === null) {
    return (
      <span
        className={cn(
          segmentClass,
          idleLabelClass,
          ACTIVE_FILTERS_BAR_SEGMENT_INNER_PAD_CLASS,
        )}
      >
        <ActiveFiltersBarSortStatusIcon
          sortDirection={null}
          className="h-3.5 w-3.5 shrink-0 opacity-70"
        />
        <span className="hidden sm:inline">{noneActiveSortingLabel}</span>
        <span className="sr-only sm:hidden">{noneActiveSortingLabel}</span>
      </span>
    );
  }

  const sortPanelFooter = (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="h-8 w-full text-xs"
      onClick={handleClearSorting}
    >
      {t("table.clearSorting")}
    </Button>
  );

  const sortPanelBody = (
    <div className="flex flex-col gap-3">
      <p className="text-sm leading-snug text-foreground">{sortSummaryLine}</p>
      {sortPanelFooter}
    </div>
  );

  const triggerButton = (
    <button
      type="button"
      className={cn(
        segmentClass,
        ACTIVE_FILTERS_BAR_SEGMENT_INNER_PAD_CLASS,
        "text-left outline-none transition-colors",
        triggerInteractionsClass,
      )}
      title={sortSummaryLine}
    >
      <ActiveFiltersBarSortStatusIcon
        sortDirection={sortDirection}
        className={cn(activeGlyphClass, "h-3.5 w-3.5 shrink-0")}
      />
      <span className="tabular-nums sm:hidden">1</span>
      <span className="hidden min-w-0 truncate underline decoration-dotted underline-offset-2 sm:inline">
        {sortingActiveBriefLabel}
      </span>
    </button>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>{triggerButton}</SheetTrigger>
        <SheetContent
          side="bottom"
          className={cn(
            "flex flex-col gap-0 overflow-hidden rounded-t-xl border-0 p-0 pb-[env(safe-area-inset-bottom)]",
            "[&>button.absolute]:right-3 [&>button.absolute]:top-3.5",
          )}
        >
          <div className="shrink-0 border-b px-4 pb-3 pt-4 pr-12">
            <SheetTitle className="text-left text-base font-medium leading-tight">
              {sortSummaryLine}
            </SheetTitle>
          </div>
          <div className="p-3 pt-4">{sortPanelFooter}</div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
      <PopoverContent className="w-[min(100vw-2rem,20rem)] p-3" align="start">
        {sortPanelBody}
      </PopoverContent>
    </Popover>
  );
};

export const ActiveFiltersBarSortSlot = memo(ActiveFiltersBarSortSlotComp);
